import { AssetsClientError } from './errors';
import type { AssetType, AssetUploadFile } from './types';

export const MAX_SIZE_BY_ASSET_TYPE: Record<AssetType, number> = {
  image: 25 * 1024 * 1024,
  video: 500 * 1024 * 1024,
  document: 20 * 1024 * 1024,
};

export const ALLOWED_MIME_BY_ASSET_TYPE: Record<AssetType, string[]> = {
  image: [
    'image/jpeg',
    'image/png',
    'image/webp',
    'image/avif',
    'image/gif',
    'image/svg+xml',
    'image/heic',
    'image/heif',
    'image/heic-sequence',
    'image/heif-sequence',
  ],
  video: ['video/mp4', 'video/webm', 'video/quicktime'],
  document: [
    'application/pdf',
    'application/msword',
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
    'application/vnd.ms-excel',
    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    'application/vnd.ms-powerpoint',
    'application/vnd.openxmlformats-officedocument.presentationml.presentation',
    'text/csv',
    'text/plain',
    'application/zip',
  ],
};

export interface AssetPolicyValidationResult {
  ok: boolean;
  reason?: string;
}

function normalizeMimeType(mimeType: string): string {
  return mimeType.split(';')[0]?.trim().toLowerCase();
}

export function validateAssetFilePolicy(args: {
  file: AssetUploadFile;
  assetType: AssetType;
}): AssetPolicyValidationResult {
  const mimeType = normalizeMimeType(args.file.type);
  const allowedMimes = ALLOWED_MIME_BY_ASSET_TYPE[args.assetType];

  if (!mimeType || !allowedMimes.includes(mimeType)) {
    return {
      ok: false,
      reason: `Unsupported file type for ${args.assetType}. Allowed: ${allowedMimes.join(', ')}`,
    };
  }

  const maxSize = MAX_SIZE_BY_ASSET_TYPE[args.assetType];
  if (args.file.size > maxSize) {
    return {
      ok: false,
      reason: `${args.assetType} exceeds max upload size of ${Math.round(maxSize / (1024 * 1024))}MB`,
    };
  }

  return { ok: true };
}

export function assertAssetFilePolicy(args: { file: AssetUploadFile; assetType: AssetType }): void {
  const result = validateAssetFilePolicy(args);
  if (!result.ok) {
    throw new AssetsClientError({
      code: 'VALIDATION_FAILED',
      message: result.reason ?? 'File validation failed',
      details: {
        assetType: args.assetType,
        file: {
          name: args.file.name,
          type: args.file.type,
          size: args.file.size,
        },
      },
    });
  }
}
